import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { getAdaptiveConcurrency } from 'shared/utils/upload-resilience';
import {
  QueueItem,
  QueueMode,
  QueueStats,
  UploadQueueContextValue,
} from './types';

const MAX_PARALLEL_UPLOADS = 4;

type InternalQueueItem<T> = QueueItem<T> & {
  resolve: (value: T) => void;
  reject: (reason?: unknown) => void;
};

const UploadQueueContext = createContext<UploadQueueContextValue | null>(null);

const UploadQueueProvider: React.FC<PropsWithChildren<{}>> = ({ children }) => {
  const [queue, setQueue] = useState<Array<InternalQueueItem<unknown>>>([]);
  const [activeIds, setActiveIds] = useState<Set<string>>(new Set());
  const [mode, setMode] = useState<QueueMode>('parallel');

  const startItem = useCallback((item: InternalQueueItem<unknown>) => {
    setActiveIds(prev => {
      const next = new Set(prev);
      next.add(item.id);
      return next;
    });

    item
      .task()
      .then(item.resolve)
      .catch(item.reject)
      .finally(() => {
        setActiveIds(prev => {
          const next = new Set(prev);
          next.delete(item.id);
          return next;
        });
      });
  }, []);

  useEffect(() => {
    const allowedConcurrency = Math.max(
      1,
      getAdaptiveConcurrency(MAX_PARALLEL_UPLOADS)
    );
    setMode(allowedConcurrency === 1 ? 'sequential' : 'parallel');

    if (queue.length === 0) return;

    const availableSlots = Math.max(0, allowedConcurrency - activeIds.size);
    if (availableSlots === 0) return;

    const itemsToStart = queue.slice(0, availableSlots);
    if (itemsToStart.length === 0) return;

    setQueue(prev => prev.slice(itemsToStart.length));
    itemsToStart.forEach(startItem);
  }, [activeIds.size, queue, startItem]);

  const enqueueUpload = useCallback(
    <T,>(item: QueueItem<T>) =>
      new Promise<T>((resolve, reject) => {
        setQueue(prev => [
          ...prev,
          {
            ...item,
            resolve: resolve as (value: unknown) => void,
            reject: reject as (reason?: unknown) => void,
          },
        ]);
      }),
    []
  );

  const stats: QueueStats = useMemo(
    () => ({
      pending: queue.length,
      active: activeIds.size,
      mode,
      enabled: true,
    }),
    [activeIds.size, mode, queue.length]
  );

  const contextValue = useMemo<UploadQueueContextValue>(
    () => ({
      enqueueUpload,
      stats,
    }),
    [enqueueUpload, stats]
  );

  return (
    <UploadQueueContext.Provider value={contextValue}>
      {children}
    </UploadQueueContext.Provider>
  );
};

export { UploadQueueProvider };
export default UploadQueueContext;
